import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Lock, CreditCard, Loader2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const FEATURES_FREE = [
  "Calendario visite illimitato",
  "Gestione clienti",
  "Registro esiti e ordini",
  "Notifiche push appuntamenti",
  "App installabile (PWA)",
];

const FEATURES_PREMIUM = [
  "Statistiche avanzate e report ordini",
  "Monitoraggio target per cliente",
  "Galleria biglietti da visita",
  "Import clienti da Excel",
  "Google Maps clienti (prossimamente)",
  "Accesso prioritario a nuove funzioni",
];

export default function PremiumPage() {
  const { isPremium, subscription, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paying, setPaying] = useState(false);
  const [checking, setChecking] = useState(false);

  // Polling dopo ritorno da Stripe
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    setChecking(true);

    let attempts = 0;
    const poll = setInterval(async () => {
      attempts++;
      try {
        const res = await axios.get(`${API}/api/payments/status/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data.payment_status === "paid") {
          clearInterval(poll);
          setChecking(false);
          await refreshUser();
          toast.success("Premium attivato! Benvenuto.");
          navigate("/app/premium", { replace: true });
        }
      } catch {}
      if (attempts >= 6) { clearInterval(poll); setChecking(false); }
    }, 2000);

    return () => clearInterval(poll);
  }, [searchParams, token, refreshUser, navigate]);

  const handlePay = async () => {
    setPaying(true);
    try {
      const res = await axios.post(`${API}/api/payments/create-checkout`, {},
        { headers: { Authorization: `Bearer ${token}`, "origin": window.location.origin } }
      );
      window.location.href = res.data.url;
    } catch {
      toast.error("Errore nell'avvio del pagamento. Riprova.");
      setPaying(false);
    }
  };

  if (isPremium) {
    return (
      <div className="p-4 md:p-6 max-w-lg mx-auto text-center">
        <div className="w-16 h-16 rounded-2xl bg-emerald-500 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-2xl font-extrabold text-[#0F172A] mb-2">
          {subscription === "business" ? "Business attivo" : "Premium attivo"}
        </h1>
        <p className="text-slate-500 text-sm mb-6">
          Hai accesso a tutte le funzioni {subscription === "business" ? "Business" : "Premium"}.
        </p>
        <button onClick={() => navigate(-1)} className="bg-[#0F172A] text-white px-8 py-3 rounded-xl font-bold text-sm">
          Torna all'app
        </button>
      </div>
    );
  }

  // Schermata "pagamento in verifica"
  if (checking) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="w-10 h-10 text-violet-500 animate-spin" />
        <p className="text-[#0F172A] font-semibold">Verifica pagamento in corso…</p>
        <p className="text-slate-400 text-sm">Attendi qualche secondo</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-lg mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-[#0F172A] flex items-center justify-center mx-auto mb-4">
          <Lock className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-2xl font-extrabold text-[#0F172A] mb-1">venditoreElite Premium</h1>
        <p className="text-slate-500 text-sm">Sblocca tutte le funzioni avanzate</p>
      </div>

      {/* Price card */}
      <div className="bg-[#0F172A] rounded-2xl p-6 mb-6 text-center text-white">
        <div className="text-4xl font-extrabold mb-1">€4,90</div>
        <div className="text-white/50 text-sm">al mese · disdici quando vuoi</div>
      </div>

      {/* Features grid */}
      <div className="space-y-4 mb-8">
        <h2 className="font-semibold text-[#0F172A] text-sm">Piano Gratuito (attuale)</h2>
        <div className="space-y-2">
          {FEATURES_FREE.map(f => (
            <div key={f} className="flex items-center gap-2 text-sm text-slate-600">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              {f}
            </div>
          ))}
        </div>

        <h2 className="font-semibold text-[#0F172A] text-sm pt-2">Piano Premium — €4,90/mese</h2>
        <div className="space-y-2">
          {FEATURES_PREMIUM.map(f => (
            <div key={f} className="flex items-center gap-2 text-sm text-slate-600">
              <CheckCircle2 className="w-4 h-4 text-violet-500 flex-shrink-0" />
              {f}
            </div>
          ))}
        </div>
      </div>

      {/* CTA pagamento */}
      <button
        onClick={handlePay}
        disabled={paying}
        data-testid="pay-premium-btn"
        className="w-full bg-[#0F172A] text-white font-bold py-4 rounded-xl text-base flex items-center justify-center gap-2 hover:bg-slate-800 transition active:scale-95 disabled:opacity-50"
      >
        {paying ? (
          <><Loader2 className="w-5 h-5 animate-spin" />Reindirizzamento…</>
        ) : (
          <><CreditCard className="w-5 h-5" />Attiva Premium — €4,90/mese</>
        )}
      </button>
      <p className="text-center text-xs text-slate-400 mt-2">Pagamento sicuro via Stripe · Disdici quando vuoi</p>
    </div>
  );
}
